import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Loader2,
  RefreshCw,
  Play,
  Image as ImageIcon,
  ExternalLink,
  Eye,
  BarChart,
  BarChart3,
  ShoppingCart,
  TrendingUp,
  Layers,
  Type,
  Video,
  AlignLeft,
  Boxes,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

const DATE_RANGE_OPTIONS = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last14', label: 'Last 14 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

function fmtTRY(n) { return '₺' + Math.round(Number(n || 0)).toLocaleString('tr-TR'); }
function fmtNum(n) { return Number(n || 0).toLocaleString('tr-TR'); }
function fmtLarge(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(2).replace(/\.?0+$/, '') + 'M';
  if (v >= 1_000) return (v / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return Math.round(v).toLocaleString('tr-TR');
}
function safe(n) { return Number(n || 0); }

// Classify field_type into UI buckets
function bucketOf(ft) {
  const x = (ft || '').toUpperCase();
  if (x.includes('LONG_HEADLINE')) return 'long_headline';
  if (x.includes('HEADLINE')) return 'headline';
  if (x.includes('DESCRIPTION')) return 'description';
  if (x.includes('BUSINESS_NAME')) return 'business_name';
  if (x.includes('CALL_TO_ACTION')) return 'cta';
  if (x === 'YOUTUBE_VIDEO') return 'video';
  if (x.includes('LOGO')) return 'logo';
  if (x.includes('IMAGE')) return 'image';
  return 'other';
}

export default function GoogleCreativePerformance() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('last30');
  const [sort, setSort] = useState({ key: 'cost', dir: 'desc' });
  const [selectedAssetGroupId, setSelectedAssetGroupId] = useState(null);
  const [creatives, setCreatives] = useState([]);
  const [creativesLoading, setCreativesLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) { setError('No brand selected'); setGroups([]); return; }
      const resp = await axios.get(`${API_BASE}/api/google-ads/pmax-asset-groups`, {
        params: { dateRange },
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId },
      });
      setGroups(Array.isArray(resp?.data?.rows) ? resp.data.rows : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCreatives = async (assetGroupId) => {
    if (!assetGroupId) { setCreatives([]); return; }
    setCreativesLoading(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const resp = await axios.get(`${API_BASE}/api/google-ads/pmax-asset-group-creatives`, {
        params: { assetGroupId },
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId },
      });
      setCreatives(Array.isArray(resp?.data?.rows) ? resp.data.rows : []);
    } catch (e) {
      setCreatives([]);
    } finally {
      setCreativesLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateRange]);

  useEffect(() => {
    if (!selectedAssetGroupId && groups.length > 0) {
      setSelectedAssetGroupId(groups[0].asset_group_id);
    }
  }, [groups, selectedAssetGroupId]);

  useEffect(() => {
    loadCreatives(selectedAssetGroupId);
  }, [selectedAssetGroupId]);

  const enriched = useMemo(() => groups.map(g => ({
    ...g,
    cost: safe(g.cost),
    impressions: safe(g.impressions),
    clicks: safe(g.clicks),
    conversions: safe(g.conversions),
    conversion_value: safe(g.conversion_value),
    ctr: safe(g.impressions) > 0 ? safe(g.clicks) / safe(g.impressions) * 100 : 0,
    cpc: safe(g.clicks) > 0 ? safe(g.cost) / safe(g.clicks) : 0,
    cpa: safe(g.conversions) > 0 ? safe(g.cost) / safe(g.conversions) : 0,
    roas: safe(g.cost) > 0 ? safe(g.conversion_value) / safe(g.cost) : 0,
  })), [groups]);

  const sorted = useMemo(() => {
    const k = sort.key;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...enriched].sort((a, b) => ((a[k] ?? 0) - (b[k] ?? 0)) * dir);
  }, [enriched, sort]);

  const selected = useMemo(
    () => sorted.find(g => g.asset_group_id === selectedAssetGroupId) || sorted[0] || null,
    [sorted, selectedAssetGroupId]
  );

  const totals = useMemo(() => sorted.reduce((acc, g) => {
    acc.cost += g.cost;
    acc.impressions += g.impressions;
    acc.clicks += g.clicks;
    acc.conversions += g.conversions;
    acc.conversion_value += g.conversion_value;
    return acc;
  }, { cost: 0, impressions: 0, clicks: 0, conversions: 0, conversion_value: 0 }), [sorted]);

  const totalRoas = totals.cost > 0 ? totals.conversion_value / totals.cost : 0;

  // Group selected asset group's creatives by bucket
  const creativesByBucket = useMemo(() => {
    const out = { headline: [], long_headline: [], description: [], business_name: [], cta: [], image: [], video: [], logo: [], other: [] };
    for (const c of creatives) {
      const b = bucketOf(c.field_type);
      out[b].push(c);
    }
    return out;
  }, [creatives]);

  const SortableTh = ({ k, label }) => (
    <th
      className="py-2 px-3 text-right cursor-pointer select-none"
      onClick={() => setSort(prev => ({ key: k, dir: prev.key === k && prev.dir === 'desc' ? 'asc' : 'desc' }))}
    >
      <span className="inline-flex items-center gap-1">
        {label}{sort.key === k && <span>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );

  return (
    <div className="min-h-screen p-4 space-y-6 max-w-screen-2xl mx-auto w-full overflow-x-hidden bg-[#f6f8ff]/70">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-5 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-50 border border-blue-100 shadow-sm rounded-xl">
            <img src="/google_ads-512.webp" alt="Google Ads" className="w-8 h-8 object-contain" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Creative Performance</h1>
            <p className="text-sm text-gray-500 mt-1">Performance Max asset groups &amp; their creative composition</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <select
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-blue-100 outline-none transition-all hover:border-gray-300"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            {DATE_RANGE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Performance summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Impressions', value: fmtLarge(totals.impressions), icon: Eye, color: 'blue' },
          { label: 'Revenue', value: fmtTRY(totals.conversion_value), icon: BarChart, color: 'emerald' },
          { label: 'Spend', value: fmtTRY(totals.cost), icon: BarChart3, color: 'indigo' },
          { label: 'Conversions', value: fmtLarge(totals.conversions), icon: ShoppingCart, color: 'purple' },
          { label: 'ROAS', value: totalRoas.toFixed(2) + 'x', icon: TrendingUp, color: 'rose' },
        ].map((m, i) => (
          <div key={i} className="p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow bg-white">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl bg-${m.color}-50`}>
                  <m.icon className={`w-5 h-5 text-${m.color}-600`} />
                </div>
                <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-full">
                  {DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label || ''}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">{m.label}</p>
                <p className="text-2xl font-bold text-gray-900">{m.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{error}</div>
      )}

      {/* Two-column layout */}
      <div className="flex flex-col lg:flex-row gap-6 relative">
        {/* Left: asset groups list */}
        <div className="w-full lg:w-7/12 space-y-6">
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
            <div className="p-4 border-b bg-white text-gray-800 font-medium flex justify-between items-center">
              <span className="text-sm font-medium">Asset Groups (Performance Max)</span>
              <span className="text-xs text-gray-500">Total: {sorted.length}</span>
            </div>
            <div className="overflow-x-auto">
              <div className="max-h-[600px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-left text-gray-500 font-semibold">
                      <th className="py-2 px-3">Asset Group</th>
                      <SortableTh k="cost" label="Spend" />
                      <SortableTh k="conversion_value" label="Revenue" />
                      <SortableTh k="conversions" label="Conv." />
                      <SortableTh k="clicks" label="Clicks" />
                      <SortableTh k="impressions" label="Impr." />
                      <SortableTh k="roas" label="ROAS" />
                      <th className="py-2 px-3 text-right">Assets</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr><td colSpan={8} className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                    ) : sorted.length === 0 ? (
                      <tr><td colSpan={8} className="py-10 text-center text-sm text-gray-500">No Performance Max data for this date range.</td></tr>
                    ) : (
                      sorted.map((g) => {
                        const isSel = selected?.asset_group_id === g.asset_group_id;
                        return (
                          <tr
                            key={g.asset_group_id}
                            className={`cursor-pointer transition-colors ${isSel ? 'bg-blue-50 border-l-2 border-blue-500' : 'hover:bg-gray-50'}`}
                            onClick={() => setSelectedAssetGroupId(g.asset_group_id)}
                          >
                            <td className="py-2 px-3">
                              <div className="min-w-0">
                                <div className="text-xs font-medium text-gray-800 truncate max-w-[360px]">{g.asset_group_name || g.asset_group_id}</div>
                                <div className="text-[10px] text-gray-500 truncate max-w-[360px]">{g.campaign_name || '—'}</div>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtTRY(g.cost)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtTRY(g.conversion_value)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtNum(g.conversions)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtNum(g.clicks)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtNum(g.impressions)}</td>
                            <td className={`py-2 px-3 text-right tabular-nums font-medium ${g.roas >= 2 ? 'text-emerald-600' : g.roas >= 1 ? 'text-amber-600' : 'text-gray-500'}`}>
                              {g.roas > 0 ? g.roas.toFixed(2) + 'x' : '—'}
                            </td>
                            <td className="py-2 px-3 text-right text-[10px] text-gray-500">
                              <span title="Total assets">{g.total_asset_count}</span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Right: asset group composition */}
        <div className="w-full lg:w-5/12 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {selected ? (
              <>
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Performance Max · Asset Group</div>
                  <div className="text-sm font-semibold text-gray-900 mt-0.5">{selected.asset_group_name || selected.asset_group_id}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{selected.campaign_name || '—'}</div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                    <Metric label="Spend" value={fmtTRY(selected.cost)} />
                    <Metric label="Revenue" value={fmtTRY(selected.conversion_value)} />
                    <Metric label="ROAS" value={selected.roas > 0 ? selected.roas.toFixed(2) + 'x' : '—'}
                      highlight={selected.roas >= 2 ? 'emerald' : selected.roas >= 1 ? 'amber' : null} />
                    <Metric label="Impressions" value={fmtNum(selected.impressions)} />
                    <Metric label="Clicks" value={fmtNum(selected.clicks)} />
                    <Metric label="Conversions" value={fmtNum(selected.conversions)} />
                  </div>
                </div>

                <div className="p-4 max-h-[640px] overflow-y-auto">
                  {creativesLoading ? (
                    <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-gray-400" /></div>
                  ) : creatives.length === 0 ? (
                    <div className="py-6 text-center text-xs text-gray-500">No creative assets found for this group.</div>
                  ) : (
                    <div className="space-y-4">
                      {creativesByBucket.image.length > 0 && (
                        <Section title="Images" icon={ImageIcon} count={creativesByBucket.image.length}>
                          <div className="grid grid-cols-3 gap-2">
                            {creativesByBucket.image.slice(0, 18).map((a) => (
                              <a
                                key={a.asset_id}
                                href={a.image_url || '#'}
                                target="_blank"
                                rel="noreferrer"
                                className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group"
                                title={a.field_type}
                              >
                                {a.image_url ? (
                                  <img src={a.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-300">
                                    <ImageIcon className="w-5 h-5" />
                                  </div>
                                )}
                                <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[8px] px-1 py-0.5 truncate text-center opacity-0 group-hover:opacity-100 transition">
                                  {a.field_type}
                                </div>
                              </a>
                            ))}
                          </div>
                        </Section>
                      )}

                      {creativesByBucket.video.length > 0 && (
                        <Section title="YouTube Videos" icon={Video} count={creativesByBucket.video.length}>
                          <div className="grid grid-cols-2 gap-2">
                            {creativesByBucket.video.slice(0, 8).map((a) => (
                              <a
                                key={a.asset_id}
                                href={a.youtube_video_id ? `https://www.youtube.com/watch?v=${a.youtube_video_id}` : '#'}
                                target="_blank"
                                rel="noreferrer"
                                className="block relative bg-gray-900 rounded-lg overflow-hidden aspect-video group"
                              >
                                {a.youtube_video_id ? (
                                  <img
                                    src={`https://img.youtube.com/vi/${a.youtube_video_id}/mqdefault.jpg`}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                                    <Video className="w-6 h-6" />
                                  </div>
                                )}
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition">
                                  <div className="bg-white/95 rounded-full p-2"><Play className="w-4 h-4 text-gray-900" fill="currentColor" /></div>
                                </div>
                                {a.youtube_video_title && (
                                  <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[10px] px-1.5 py-1 truncate">
                                    {a.youtube_video_title}
                                  </div>
                                )}
                              </a>
                            ))}
                          </div>
                        </Section>
                      )}

                      {creativesByBucket.headline.length > 0 && (
                        <TextSection title="Headlines" icon={Type} items={creativesByBucket.headline} />
                      )}
                      {creativesByBucket.long_headline.length > 0 && (
                        <TextSection title="Long Headlines" icon={Type} items={creativesByBucket.long_headline} />
                      )}
                      {creativesByBucket.description.length > 0 && (
                        <TextSection title="Descriptions" icon={AlignLeft} items={creativesByBucket.description} />
                      )}
                      {creativesByBucket.business_name.length > 0 && (
                        <TextSection title="Business Names" icon={Boxes} items={creativesByBucket.business_name} />
                      )}
                      {creativesByBucket.logo.length > 0 && (
                        <Section title="Logos" icon={ImageIcon} count={creativesByBucket.logo.length}>
                          <div className="flex flex-wrap gap-2">
                            {creativesByBucket.logo.slice(0, 6).map((a) => (
                              <div key={a.asset_id} className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                                {a.image_url ? <img src={a.image_url} alt="" className="w-full h-full object-contain" /> : <ImageIcon className="w-5 h-5 text-gray-300" />}
                              </div>
                            ))}
                          </div>
                        </Section>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="p-10 text-center text-sm text-gray-400">Select an asset group to view its creative composition</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, highlight }) {
  return (
    <div className="bg-gray-50 rounded-lg p-2">
      <div className="text-[9px] uppercase text-gray-500 font-medium">{label}</div>
      <div className={`text-xs font-semibold mt-0.5 ${
        highlight === 'emerald' ? 'text-emerald-600' : highlight === 'amber' ? 'text-amber-600' : 'text-gray-900'
      }`}>{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
        <Icon className="w-3.5 h-3.5" />
        <span>{title}</span>
        <span className="text-gray-400 font-normal">· {count}</span>
      </div>
      {children}
    </div>
  );
}

function TextSection({ title, icon: Icon, items }) {
  return (
    <Section title={title} icon={Icon} count={items.length}>
      <div className="space-y-1">
        {items.slice(0, 12).map((a) => (
          <div key={a.asset_id} className="bg-gray-50 rounded-md px-2 py-1.5 text-xs text-gray-800">
            {a.asset_text || a.asset_name || <span className="text-gray-400 italic">(no text)</span>}
          </div>
        ))}
        {items.length > 12 && (
          <div className="text-[10px] text-gray-400 text-center pt-1">+{items.length - 12} more</div>
        )}
      </div>
    </Section>
  );
}
